"use client";

/**
 * Cab-app style dispatch alerts for the web.
 * True background push needs FCM + a server. While the mistri stays online in
 * this tab (or the PWA), we ring, vibrate, wake the screen and keep a sticky
 * system notification until they Accept or Skip.
 */

const TITLE_IDLE = "Mistry Bhai";
let audioCtx: AudioContext | null = null;
let ringTimer: number | null = null;
let notifyTimer: number | null = null;
let titleTimer: number | null = null;
let wakeLock: { release: () => Promise<void> } | null = null;
let ringing = false;
let lastCount = 0;
let lastHeadline = "";

function canNotify() {
  return typeof Notification !== "undefined" && Notification.permission === "granted";
}

export async function ensureDispatchPermission() {
  if (typeof Notification === "undefined") return "denied" as NotificationPermission;
  if (Notification.permission !== "default") return Notification.permission;
  return Notification.requestPermission();
}

async function unlockAudio() {
  if (typeof window === "undefined") return;
  const Ctx = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return;
  if (!audioCtx) audioCtx = new Ctx();
  if (audioCtx.state === "suspended") await audioCtx.resume().catch(() => undefined);
}

function beepOnce() {
  if (!audioCtx) return;
  const t = audioCtx.currentTime;
  const burst = (freq: number, at: number, hold: number) => {
    const osc = audioCtx!.createOscillator();
    const gain = audioCtx!.createGain();
    osc.type = "square";
    osc.frequency.setValueAtTime(freq, at);
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(0.14, at + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + hold);
    osc.connect(gain);
    gain.connect(audioCtx!.destination);
    osc.start(at);
    osc.stop(at + hold + 0.02);
  };
  burst(880, t, 0.16);
  burst(1174, t + 0.2, 0.16);
  burst(880, t + 0.4, 0.22);
}

let userGestured = false;

export function markUserGestured() {
  userGestured = true;
}

function vibrate() {
  if (!userGestured) return;
  try {
    navigator.vibrate?.([220, 80, 220, 80, 400]);
  } catch {
    // ignore — Chrome blocks until user taps the page
  }
}

async function showStickyNotification(count: number, headline: string) {
  if (!canNotify() || count <= 0) return;
  const title = count === 1 ? "New roadside SOS" : `${count} customers waiting`;
  const options: NotificationOptions = {
    body: headline,
    tag: "mistry-dispatch",
    renotify: true,
    requireInteraction: true,
    silent: false,
    data: { open: "partner" },
  };
  try {
    const registration = await navigator.serviceWorker?.ready;
    if (registration?.showNotification) {
      await registration.showNotification(title, options);
      return;
    }
  } catch {
    // fall through to page Notification
  }
  try {
    new Notification(title, options);
  } catch {
    // blocked
  }
}

async function clearStickyNotification() {
  try {
    const registration = await navigator.serviceWorker?.ready;
    const notes = await registration?.getNotifications?.({ tag: "mistry-dispatch" });
    notes?.forEach((note) => note.close());
  } catch {
    // ignore
  }
}

function pulseTitle(count: number) {
  if (titleTimer) window.clearInterval(titleTimer);
  if (count <= 0) {
    document.title = TITLE_IDLE;
    titleTimer = null;
    return;
  }
  let on = true;
  document.title = `(${count}) New SOS — Mistry Bhai`;
  titleTimer = window.setInterval(() => {
    document.title = on ? "● SOS waiting — tap Accept" : `(${count}) New SOS — Mistry Bhai`;
    on = !on;
  }, 900);
}

async function holdScreenAwake() {
  const nav = navigator as Navigator & { wakeLock?: { request: (type: "screen") => Promise<{ release: () => Promise<void> }> } };
  if (!nav.wakeLock) return;
  try {
    wakeLock = await nav.wakeLock.request("screen");
  } catch {
    wakeLock = null;
  }
}

function releaseScreen() {
  void wakeLock?.release().catch(() => undefined);
  wakeLock = null;
}

export async function armDispatchAlerts() {
  await unlockAudio();
  await holdScreenAwake();
}

export function startDispatchRing(count: number, headline: string) {
  lastCount = count;
  lastHeadline = headline;
  if (count <= 0) {
    stopDispatchRing();
    return;
  }
  pulseTitle(count);
  vibrate();
  void showStickyNotification(count, headline);
  if (ringing) return;
  ringing = true;
  void unlockAudio().then(() => beepOnce());
  ringTimer = window.setInterval(() => {
    beepOnce();
    vibrate();
  }, 2200);
  notifyTimer = window.setInterval(() => {
    void showStickyNotification(lastCount, lastHeadline);
  }, 8000);
}

export function stopDispatchRing() {
  ringing = false;
  lastCount = 0;
  lastHeadline = "";
  if (ringTimer) window.clearInterval(ringTimer);
  if (notifyTimer) window.clearInterval(notifyTimer);
  if (titleTimer) window.clearInterval(titleTimer);
  ringTimer = notifyTimer = titleTimer = null;
  document.title = TITLE_IDLE;
  void clearStickyNotification();
}

export function disarmDispatchAlerts() {
  stopDispatchRing();
  releaseScreen();
}
